/**
 * Languages List View
 *
 * Main languages management page with flag toggles
 *
 * Story 11.17 - Consolidated voice configuration to locales
 */

import toast from 'react-hot-toast';
import {
  useLanguages,
  useUpdateDisplayLocalizationProvider,
  useUpdateLanguageFlags,
  useUpdateWaitlistEnabled,
  useUpdateWordsEnabled,
} from '../services/languagesApi';
import type { UpdateDisplayLocalizationProviderRequest } from '../services/languagesApi';
import { DictionariesNavigation } from '../components/DictionariesNavigation';

export function LanguagesListView() {
  const { data: languages, isLoading, error } = useLanguages();
  const updateLanguageFlagsMutation = useUpdateLanguageFlags();
  const updateWordsEnabledMutation = useUpdateWordsEnabled();
  const updateWaitlistEnabledMutation = useUpdateWaitlistEnabled();
  const updateDisplayLocalizationProviderMutation = useUpdateDisplayLocalizationProvider();

  const handleToggleSupportedAsNative = async (languageCode: string, currentValue: boolean) => {
    try {
      await updateLanguageFlagsMutation.mutateAsync({
        languageCode,
        request: { supportedAsNative: !currentValue },
      });
      toast.success(
        `Language ${!currentValue ? 'enabled' : 'disabled'} as native language successfully!`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update language flags';
      toast.error(errorMessage);
    }
  };

  const handleToggleWordsEnabled = async (languageCode: string, currentValue: boolean) => {
    try {
      await updateWordsEnabledMutation.mutateAsync({
        languageCode,
        request: { wordsEnabled: !currentValue },
      });
      toast.success(
        `Word lists ${!currentValue ? 'enabled' : 'disabled'} for this language successfully!`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update words enabled';
      toast.error(errorMessage);
    }
  };

  const handleToggleWaitlistEnabled = async (languageCode: string, currentValue: boolean) => {
    try {
      await updateWaitlistEnabledMutation.mutateAsync({
        languageCode,
        request: { waitlistEnabled: !currentValue },
      });
      toast.success(
        `Waitlist ${!currentValue ? 'enabled' : 'disabled'} for this language successfully!`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update waitlist enabled';
      toast.error(errorMessage);
    }
  };

  const handleDisplayLocalizationProviderChange = async (
    languageCode: string,
    newValue: 'llm' | 'translate'
  ) => {
    try {
      const request: UpdateDisplayLocalizationProviderRequest = {
        displayLocalizationProvider: newValue,
      };
      await updateDisplayLocalizationProviderMutation.mutateAsync({
        languageCode,
        request,
      });
      toast.success(`Localization provider updated to "${newValue.toUpperCase()}" successfully!`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update localization provider';
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading languages</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  const displayLanguages = languages || [];

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Language Management</h1>
      </div>

      {/* Languages Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="languages-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Flag
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Native Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Supported As Native
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Supported As Target
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Words Enabled
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Waitlist Enabled
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Localization
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayLanguages.map((language) => (
              <tr key={language.code} data-testid={`language-row-${language.code}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{language.code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-lg">{language.flagEmoji || '—'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{language.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{language.nativeName}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() =>
                      handleToggleSupportedAsNative(language.code, language.supportedAsNative)
                    }
                    disabled={updateLanguageFlagsMutation.isPending}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      language.supportedAsNative
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`toggle-native-${language.code}`}
                  >
                    {language.supportedAsNative ? 'Yes' : 'No'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      language.supportedAsTarget
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {language.supportedAsTarget ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleWordsEnabled(language.code, language.wordsEnabled)}
                    disabled={updateWordsEnabledMutation.isPending}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      language.wordsEnabled
                        ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`toggle-words-${language.code}`}
                  >
                    {language.wordsEnabled ? 'Yes' : 'No'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() =>
                      handleToggleWaitlistEnabled(language.code, language.waitlistEnabled)
                    }
                    disabled={updateWaitlistEnabledMutation.isPending}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      language.waitlistEnabled
                        ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`toggle-waitlist-${language.code}`}
                  >
                    {language.waitlistEnabled ? 'Yes' : 'No'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={language.displayLocalizationProvider}
                    onChange={(e) =>
                      handleDisplayLocalizationProviderChange(
                        language.code,
                        e.target.value as 'llm' | 'translate'
                      )
                    }
                    disabled={updateDisplayLocalizationProviderMutation.isPending}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`select-localization-${language.code}`}
                  >
                    <option value="llm">LLM</option>
                    <option value="translate">Translate</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {displayLanguages.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg">No languages configured in the system.</p>
        </div>
      )}
    </div>
  );
}
